import React, { useState, useRef, useEffect } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@frontend/shadcn/components/ui/form';
import { Control, Path, FieldValues } from 'react-hook-form';
import { CheckIcon, BoltIcon, XmarkIcon } from '@revfluence/fresh-icons/solid/esm';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '@frontend/shadcn/lib/utils';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandInput,
  CommandList,
} from '@frontend/shadcn/components/ui/command';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from '@frontend/shadcn/components/ui/dropdown-menu';

// Import the enforced type from index (avoiding circular import by using relative type import)
type DynamicOptions = Record<`{{${string}}}`, string>;

interface GenericUtmFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  label: string;
  placeholder?: string;
  utmType?: 'source' | 'medium' | 'campaign' | 'content' | 'term';
  dynamicOptions?: DynamicOptions; // Now enforces {{ }} format
}

// Common static values for different UTM parameters
const COMMON_UTM_VALUES: Record<string, string[]> = {
  source: ['facebook', 'instagram', 'google', 'twitter', 'linkedin', 'tiktok', 'youtube'],
  medium: ['cpc', 'social', 'email', 'referral', 'organic', 'display', 'affiliate'],
  campaign: ['spring_sale', 'summer_promo', 'black_friday', 'holiday_campaign', 'product_launch'],
  content: ['ad_1', 'ad_2', 'banner', 'video', 'carousel', 'story'],
  term: ['branded', 'generic', 'competitor', 'product_name'],
};

export function GenericUtmField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder = 'Enter value or select dynamic parameter',
  utmType,
  dynamicOptions,
}: GenericUtmFieldProps<TFieldValues>) {
  const [open, setOpen] = useState(false);
  const commandInputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>('');

  // Helper function to check if a value is a dynamic placeholder
  const isDynamicPlaceholder = (value: string): boolean => {
    return Object.keys(dynamicOptions || {}).includes(value);
  };

  // Get common values for this specific UTM type
  const getCommonValues = (): string[] => {
    return utmType ? COMMON_UTM_VALUES[utmType] || [] : [];
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (commandInputRef.current) {
          commandInputRef.current.focus({ preventScroll: true });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const committedFieldValue = typeof field.value === 'string' ? field.value : '';

        const handleCustomValueCommit = (valueToCommit: string) => {
          const trimmedValue = valueToCommit.trim();
          field.onChange(trimmedValue);
          setOpen(false);
        };

        const handleClearSelection = (e?: React.MouseEvent) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation(); // Prevent dropdown from opening when clear button is clicked
          }
          field.onChange('');
          setOpen(false);
        };

        const handleOpenChange = (newOpenState: boolean) => {
          if (newOpenState) {
            setInputValue(committedFieldValue);
          }
          setOpen(newOpenState);
        };

        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className="relative">
                <DropdownMenu open={open} onOpenChange={handleOpenChange}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      className="w-full justify-between h-9 py-1 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {committedFieldValue ? (
                          isDynamicPlaceholder(committedFieldValue) ? (
                            // Dynamic placeholder styling
                            <div className="flex items-center gap-1 min-w-0">
                              <Badge
                                variant="secondary"
                                className="bg-blue-100 text-blue-700 border-blue-200 px-1 py-0 text-xs font-mono flex-shrink-0 h-4"
                              >
                                <BoltIcon className="h-2.5 w-2.5 mr-0.5" />
                                Dyn
                              </Badge>
                              <span className="font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded text-xs truncate">
                                {committedFieldValue}
                              </span>
                            </div>
                          ) : (
                            // Static value styling
                            <span className="text-gray-900 truncate font-medium text-sm">{committedFieldValue}</span>
                          )
                        ) : (
                          // Placeholder text
                          <span className="text-gray-500 truncate text-sm">{placeholder}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        {/* Reserve space for clear button when present */}
                        {committedFieldValue && <div className="w-4 h-4" />}
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>

                  {/* Clear button positioned absolutely outside the trigger */}
                  {committedFieldValue && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 p-0 hover:bg-red-100 hover:text-red-600 text-gray-400 z-10"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClearSelection(e);
                      }}
                      title="Clear selection"
                      aria-label="Clear UTM parameter"
                    >
                      <XmarkIcon className="h-3 w-3" />
                    </Button>
                  )}
                  <DropdownMenuContent
                    className="w-[var(--radix-dropdown-menu-trigger-width)] p-0 z-[200] pointer-events-auto"
                    align="start"
                  >
                    <Command>
                      <CommandInput
                        ref={commandInputRef}
                        placeholder="Search parameters or type custom value..."
                        value={inputValue}
                        onValueChange={setInputValue}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCustomValueCommit(inputValue);
                          }
                        }}
                      />
                      <CommandList className="max-h-[180px]">
                        <CommandEmpty>
                          <div className="flex flex-col items-center py-3 text-center">
                            <span className="text-sm text-gray-500 mb-1">No matching parameters found</span>
                            {inputValue.trim() !== '' && (
                              <span className="text-xs text-blue-600">
                                Press Enter to use "{inputValue.trim()}" as custom value
                              </span>
                            )}
                          </div>
                        </CommandEmpty>

                        {/* Dynamic Parameters Section */}
                        <CommandGroup heading="✨ Dynamic Parameters">
                          {Object.entries(dynamicOptions || {}).map(([key, description]) => (
                            <CommandItem
                              key={key}
                              value={key}
                              onSelect={() => {
                                handleCustomValueCommit(key);
                              }}
                              className="flex items-start gap-2 cursor-pointer px-2 py-2 min-h-0"
                            >
                              <Badge
                                variant="secondary"
                                className="bg-blue-100 text-blue-700 border-blue-200 px-1 py-0 text-xs font-mono flex-shrink-0 h-4 mt-0.5"
                              >
                                <BoltIcon className="h-2.5 w-2.5 mr-0.5" />
                                Dyn
                              </Badge>
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded leading-tight mb-1 inline-block">
                                  {key}
                                </div>
                                <div className="text-xs text-gray-500 leading-tight">{description}</div>
                              </div>
                              {committedFieldValue === key && (
                                <CheckIcon
                                  className={cn('h-3.5 w-3.5 flex-shrink-0 text-green-600 opacity-100 mt-0.5')}
                                />
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>

                        {/* Common Static Values Section */}
                        {getCommonValues().length > 0 && (
                          <CommandGroup heading="📋 Common Values">
                            {getCommonValues().map((value) => (
                              <CommandItem
                                key={value}
                                value={value}
                                onSelect={() => {
                                  handleCustomValueCommit(value);
                                }}
                                className="flex items-center justify-between cursor-pointer px-2 py-1.5 min-h-0"
                              >
                                <span className="text-sm text-gray-700">{value}</span>
                                {committedFieldValue === value && (
                                  <CheckIcon className={cn('h-3.5 w-3.5 flex-shrink-0 text-green-600 opacity-100')} />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}

                        {/* Clear Selection Option - always show at bottom when there's a value, unfiltered */}
                        {committedFieldValue && (
                          <div className="border-t border-gray-200 mt-1">
                            <div className="p-1">
                              <div className="text-xs font-medium text-gray-500 px-2 py-1">🗑️ Clear</div>
                              <button
                                type="button"
                                onClick={() => handleClearSelection()}
                                className="w-full flex items-center gap-2 cursor-pointer px-2 py-2 min-h-0 text-red-600 hover:bg-red-50 rounded text-left text-sm"
                              >
                                <XmarkIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span>Clear selection</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </CommandList>
                    </Command>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
